import { MutableRefObject, useEffect, useRef } from 'react';
import { Collection, Map, View } from 'ol';
import Layer from 'ol/layer/Layer';
import VectorLayer from 'ol/layer/Vector';
import BaseLayer from 'ol/layer/Base';
import RenderEvent from 'ol/render/Event';
import { unByKey } from 'ol/Observable';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { useMapSources } from '@/utils/context/mapSourcesContext';
import { createContentLayers } from '@/components/Map/MapViewer/utils/config/createContentLayers';
import { setLoupeRenderView } from '@/components/Map/MapViewer/utils/mapElementsRendering/elements/utils/loupeRenderView';
import { LAYER_TYPE } from '@/components/Map/MapViewer/MapViewer.constants';
import { layersVisibilityForCurrentModelSelector } from '@/redux/layers/layers.selectors';

interface UseDiagramPreviewMapResult {
  targetRef: MutableRefObject<HTMLDivElement | null>;
  previewMapRef: MutableRefObject<Map | null>;
}

/**
 * Mounts a small, stationary `ol/Map` showing part of the current diagram,
 * reusing the exact same shared sources/style-building pipeline as the main
 * diagram map and the magnifier loupe (see `useMagnifierLoupe`) so the preview
 * always matches the real rendering (shapes, colors, borders, line styles).
 * Unlike the loupe, this map doesn't follow the cursor — it shows whatever the
 * given {@link View} points at and re-renders automatically whenever the shared
 * sources change (e.g. after the previewed entity is edited and saved).
 *
 * The caller owns the view, which is what distinguishes the previews: the
 * element preview centers on one element at max zoom, the reaction preview fits
 * every line of a reaction into the viewport.
 */
export const useDiagramPreviewMap = (view: View | undefined): UseDiagramPreviewMapResult => {
  const sources = useMapSources();
  const layersVisibility = useAppSelector(layersVisibilityForCurrentModelSelector);

  const targetRef = useRef<HTMLDivElement | null>(null);
  const previewMapRef = useRef<Map | null>(null);

  useEffect(() => {
    if (!targetRef.current || previewMapRef.current) {
      return undefined;
    }
    const previewMap = new Map({
      target: targetRef.current,
      controls: new Collection(),
      interactions: new Collection(),
    });
    previewMapRef.current = previewMap;

    // See `useMagnifierLoupe`: glyph images anchor to whichever view is
    // published during the render pass, since this map has a different center
    // than the primary map.
    const renderKeys = [
      previewMap.on('precompose', () => setLoupeRenderView(previewMap.getView())),
      previewMap.on('postcompose', () => setLoupeRenderView(null)),
    ];

    return () => {
      renderKeys.forEach(unByKey);
      setLoupeRenderView(null);
      previewMap.setTarget(undefined);
      previewMapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (view) {
      previewMapRef.current?.setView(view);
      previewMapRef.current?.updateSize();
    }
  }, [view]);

  useEffect(() => {
    const previewMap = previewMapRef.current;
    if (!previewMap) {
      return undefined;
    }
    const additionalLayers = sources.additionalLayers.map(mainLayer => {
      const layerId = mainLayer.get('id');
      const visible =
        layersVisibility[layerId] !== undefined
          ? layersVisibility[layerId]
          : mainLayer.getVisible();
      const layer = new VectorLayer({
        source: mainLayer.getSource() ?? undefined,
        zIndex: mainLayer.getZIndex(),
        visible,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      });
      layer.set('type', LAYER_TYPE.ADDITIONAL_LAYER);
      return layer;
    });
    const layers: BaseLayer[] = [...createContentLayers(sources), ...additionalLayers];

    const disableSmoothing = (event: RenderEvent): void => {
      const context = event.context as CanvasRenderingContext2D | undefined;
      if (context) {
        context.imageSmoothingEnabled = false;
      }
    };
    const restoreSmoothing = (event: RenderEvent): void => {
      const context = event.context as CanvasRenderingContext2D | undefined;
      if (context) {
        context.imageSmoothingEnabled = true;
      }
    };
    const keys = layers.flatMap(layer => [
      (layer as Layer).on('prerender', disableSmoothing),
      (layer as Layer).on('postrender', restoreSmoothing),
    ]);
    previewMap.setLayers(layers);

    return () => keys.forEach(unByKey);
  }, [sources, layersVisibility]);

  return { targetRef, previewMapRef };
};
